import { SecondaryButton } from '@shared/components/buttons';
import { Spinner } from '@shared/components/spinner';
import { useContext, useEffect, useRef, useState } from 'react';
import { AlertContext } from '../../../contexts/alert-context';
import type { ProcessingResult, ReceivingSession } from '../../../data/receiving';
import { sessionVendors } from '../../../data/receiving';
import type { UploaderConnection } from '../../../data/uploader-connection';
import { isGenericAiVendor } from '../../../data/vendor';
import { logUnexpectedApiError } from '../../../helpers/api-errors';
import { useStore } from '../../../hooks/use-store';
import { useVendor, useVendorIntegrations, useVendors } from '../../../hooks/vendors';
import type { ReceivingService } from '../../../services/receiving';
import { useCancelSession } from '../hooks/use-cancel-session';
import { CancelSessionModal } from './cancel-session-modal';
import { CompanionPickerStep } from './companion-picker-step';
import { Complete } from './complete';
import { FileUpload } from './file-upload';
import { GenericReceivingUpload } from './generic-receiving-upload';
import { OverwriteDetected } from './overwrite-detected';
import { PausedLposBusy } from './paused-lpos-busy';
import { ReviewResults } from './review-results';
import { SendingScreen } from './sending-screen';
import { VendorPicker } from './vendor-picker';
import { WaitingForLpos } from './waiting-for-lpos';
import { WaitingNudge } from './waiting-nudge';
import { WriteFailed } from './write-failed';

const FAILED_FROM_COPY: Record<string, { alert: string; retryingBody: string }> = {
  processing: {
    alert: "Couldn't process invoices. Trying again…",
    retryingBody: 'Processing invoices…',
  },
};
const FALLBACK_FAILED_COPY = {
  alert: 'Something went wrong. Trying again…',
  retryingBody: 'Trying again…',
};

// failed_from values that get their own dedicated screen rather than auto-retry.
// sending_to_lpos → WriteFailed (Retry + Abandon)
// waiting_for_lpos → OverwriteDetected (Abandon only)
const DEDICATED_FAILED_FROM = new Set(['sending_to_lpos', 'waiting_for_lpos']);

type ActionPaneProps = {
  state: string;
  context: {
    storeId: string;
    session: ReceivingSession | null;
    probeRefusedCount?: number;
    abandonBusyCount?: number;
  };
  readOnly: boolean;
  service: ReceivingService;
  // Hands a session straight to the state machine, for the responses that
  // already carry one. POST /process returns the finished `review` session
  // (the lifecycle spec's `B-->>W: HTTP 200 {session}`); feeding it here is
  // what keeps this tab moving when the matching WebSocket push is dropped —
  // API Gateway 413s any frame over 128 KB, and nothing replays a lost push.
  // Required, not optional: a forgotten callback is the bug coming back.
  applySession: (session: ReceivingSession) => void;
  companionConnected: boolean;
  companionName: string;
  companions: UploaderConnection[];
};

export function ActionPane({
  state,
  context,
  readOnly,
  service,
  applySession,
  companionConnected,
  companionName,
  companions,
}: ActionPaneProps) {
  const { vendors } = useVendors();
  const integrations = useVendorIntegrations();
  const { vendor } = useVendor(context.session?.vendor_id);
  const store = useStore();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const autoRetriedRef = useRef(false);
  const { storeId, session } = context;
  const cancelSession = useCancelSession(storeId, service);
  const { addErrorAlert } = useContext(AlertContext);

  // When the session fails, auto-retry once back to get_invoices and surface an error alert.
  // Skip auto-retry for dedicated failure screens (sending_to_lpos, waiting_for_lpos) —
  // those states have their own Retry/Abandon UI and must not be silently rewound.
  useEffect(() => {
    if (state !== 'failed' || !session || autoRetriedRef.current) {
      return;
    }

    if (DEDICATED_FAILED_FROM.has(session.failed_from ?? '')) {
      return;
    }

    autoRetriedRef.current = true;
    const copy = FAILED_FROM_COPY[session.failed_from ?? ''] ?? FALLBACK_FAILED_COPY;
    addErrorAlert(copy.alert, { autoCloseAfterTimeMS: 10_000 });
    setRetrying(true);
    service.retry(storeId, session.session_id).catch((err) => {
      logUnexpectedApiError('receiving: auto-retry failed', err);

      setRetrying(false);
    });
  }, [state, session, addErrorAlert, service, storeId]);

  if (!session) {
    return null;
  }

  switch (state) {
    case 'selecting_companion':
      return (
        <CompanionPickerStep
          connections={companions}
          storeId={storeId}
          sessionId={session.session_id}
          disabled={readOnly}
          service={service}
        />
      );
    case 'selecting_vendor':
      return (
        <VendorPicker
          vendors={vendors}
          integrations={integrations}
          storeId={storeId}
          disabled={readOnly}
          onSubmit={async (vendorIds) => {
            await service.setVendor(storeId, session.session_id, vendorIds);
          }}
        />
      );
    case 'get_invoices':
      if (!vendor) {
        return null;
      }

      if (isGenericAiVendor(vendor)) {
        return (
          <GenericReceivingUpload
            vendorName={vendor.name}
            store={store}
            disabled={readOnly}
            onUploadFile={(file) => service.uploadFile(storeId, session.session_id, file)}
            onProcess={async (intake) => {
              applySession(await service.processInvoices(storeId, session.session_id, intake));
            }}
          />
        );
      }

      return (
        <FileUpload
          vendorName={vendor.name}
          acceptedFormats={vendor.invoice_formats}
          disabled={readOnly}
          onUploadFile={(file) => service.uploadFile(storeId, session.session_id, file)}
          onProcess={async () => {
            applySession(await service.processInvoices(storeId, session.session_id, 'file'));
          }}
        />
      );
    case 'processing':
      return (
        <div
          role="status"
          className="flex flex-col items-center justify-center gap-3 py-12 text-gray-600"
          data-testid="processing-indicator"
        >
          <Spinner sizeClass="w-8 h-8" />
          <p className="text-sm">Processing invoices…</p>
        </div>
      );
    case 'failed': {
      // Dedicated failure screens — own Retry/Abandon UI, no auto-retry.
      if (session.failed_from === 'sending_to_lpos') {
        return (
          <WriteFailed
            storeId={storeId}
            sessionId={session.session_id}
            service={service}
            readOnly={readOnly}
          />
        );
      }

      if (session.failed_from === 'waiting_for_lpos') {
        return (
          <OverwriteDetected
            storeId={storeId}
            sessionId={session.session_id}
            service={service}
            readOnly={readOnly}
          />
        );
      }

      // All other failures: auto-retry spinner.
      const copy = FAILED_FROM_COPY[session.failed_from ?? ''] ?? FALLBACK_FAILED_COPY;

      return (
        <>
          <div
            role="status"
            className="flex flex-col items-center justify-center gap-3 py-12 text-gray-600"
            data-testid="processing-indicator"
          >
            <Spinner sizeClass="w-8 h-8" />
            <p className="text-sm">{retrying ? copy.retryingBody : 'Something went wrong.'}</p>
            {!retrying && (
              <SecondaryButton onClick={() => setShowCancelConfirm(true)}>
                Cancel session
              </SecondaryButton>
            )}
          </div>
          <CancelSessionModal
            visible={showCancelConfirm}
            onClose={() => setShowCancelConfirm(false)}
            onConfirm={() => {
              setShowCancelConfirm(false);
              void cancelSession(session.session_id);
            }}
            body="Are you sure you want to cancel this session? Uploaded invoices will be discarded."
          />
        </>
      );
    }

    case 'review': {
      if (!session.processing_result) {
        return null;
      }

      // Feeds the Vendor provenance column and the duplicate-item-code vendor
      // suffix — both stay hidden when the session has only one vendor
      // (ORD-395). NOT wrapped in useMemo: this sits after the `if (!session)
      // return null` above and inside a switch case, so a hook here would be
      // called conditionally (Rules of Hooks).
      const vendorNames = new Map(
        sessionVendors(session, vendors).map((v) => [v.vendor_id, v.name] as const)
      );

      return (
        <ReviewResults
          storeId={storeId}
          sessionId={session.session_id}
          session={session}
          result={session.processing_result as ProcessingResult}
          readOnly={readOnly}
          service={service}
          vendorNames={vendorNames}
        />
      );
    }

    case 'sending_to_lpos':
      // Spec §10: simple progress screen, no action. Auto-advances to
      // waiting_for_lpos, paused_lpos_busy, or failed via SESSION_UPDATED.
      return <SendingScreen />;
    case 'paused_lpos_busy':
      return (
        <PausedLposBusy
          storeId={storeId}
          sessionId={session.session_id}
          service={service}
          pausedFrom={session.paused_from}
          pausedSince={session.paused_since}
          probeRefusedCount={context.probeRefusedCount ?? 0}
          abandonBusyCount={context.abandonBusyCount ?? 0}
          readOnly={readOnly}
        />
      );
    case 'waiting_for_lpos':
      return (
        <>
          <WaitingForLpos companionConnected={companionConnected} companionName={companionName} />
          {session.waiting_since != null && (
            <WaitingNudge
              storeId={storeId}
              sessionId={session.session_id}
              service={service}
              waitingSince={session.waiting_since}
              abandonBusyCount={context.abandonBusyCount ?? 0}
              readOnly={readOnly}
            />
          )}
        </>
      );
    case 'complete':
      return <Complete storeId={storeId} />;
    default:
      return null;
  }
}
